import type {ImportState} from '@gaia/types';
import {idle as idleState} from '@gaia/types/ImportState';
import compact from 'lodash/compact';
import {useEffect} from 'react';

import type {AreaDispatcher} from './useAreas';
import type {FolderDispatcher} from './useFolders';
import type {TrackDispatcher} from './useTracks';
import type {WaypointDispatcher} from './useWaypoints';

interface UseUndoImportEffectProps {
    importState: ImportState;
    setImportState: SetState<ImportState>;
    setUndo: SetState<UndoState | undefined>;
    waypointDispatcher: WaypointDispatcher;
    trackDispatcher: TrackDispatcher;
    areaDispatcher: AreaDispatcher;
    folderDispatcher: FolderDispatcher;
    selections: SelectionState[];
    setSelections: SetState<SelectionState[]>;
    tracks: {[trackLocalId: string]: Track};
    areas: {[areaLocalId: string]: Area};
    waypoints: {[waypointLocalId: string]: Waypoint};
    onSidebarChange: (sidebar: SidebarState) => void;
}

export function useImportHasSaved({
    importState,
    setImportState,
    setUndo,
    waypointDispatcher,
    trackDispatcher,
    areaDispatcher,
    folderDispatcher,
    selections,
    setSelections,
    tracks,
    areas,
    waypoints,
    onSidebarChange,
}: UseUndoImportEffectProps) {
    useEffect(() => {
        if (importState.status !== 'saving') return;

        const selectedTracks = Object.values(tracks).filter(
            (track) => track.properties.isLatestImport && !track.properties.archived
        );

        const selectedAreas = Object.values(areas).filter(
            (area) => area.properties.isLatestImport && !area.properties.archived
        );

        const selectedWaypoints = Object.values(waypoints).filter(
            (waypoint) => waypoint.properties.isLatestImport && !waypoint.properties.archived
        );

        // As items are saved they will acquire an id
        const savedItems =
            compact(selectedTracks.map((track) => track.properties.id)).length +
            compact(selectedAreas.map((area) => area.properties.id)).length +
            compact(selectedWaypoints.map((waypoint) => waypoint.properties.id)).length;

        if (savedItems !== importState.toSave) return;

        setUndo({
            message: `You imported ${savedItems} item${savedItems > 1 ? 's' : ''}.`,
            handler: () => {
                selectedWaypoints.forEach((item) => {
                    waypointDispatcher({
                        type: 'updateProperties',
                        properties: {
                            localId: item.properties.localId,
                            deleted: true,
                            archived: true,
                        },
                    });
                });
                selectedAreas.forEach((item) => {
                    areaDispatcher({
                        type: 'updateProperties',
                        properties: {
                            localId: item.properties.localId,
                            deleted: true,
                            archived: true,
                        },
                    });
                });
                selectedTracks.forEach((item) => {
                    trackDispatcher({
                        type: 'updateProperties',
                        properties: {
                            localId: item.properties.localId,
                            deleted: true,
                            archived: true,
                        },
                    });
                });

                if (importState.folder) {
                    folderDispatcher({
                        type: 'remove',
                        data: importState.folder,
                    });
                }

                onSidebarChange({type: 'savedItems'});
            },
        });

        if (importState.folder?.id) {
            // Navigate to the new import folder once it has been saved
            onSidebarChange({
                type: 'folderDetails',
                id: importState.folder.id,
                prevSidebar: {type: 'hidden'},
            });
        }

        setSelections([]);
        setImportState(idleState);

        // Set isLatestImport to false for all saved items
        trackDispatcher({
            type: 'updateProperties',
            properties: compact(
                Object.values(tracks)
                    .filter(
                        (track) => track.properties.isLatestImport && !track.properties.archived
                    )
                    .map((track) => ({
                        localId: track.properties.localId,
                        isLatestImport: false,
                    }))
            ),
        });

        waypointDispatcher({
            type: 'update',
            data: compact(
                Object.values(waypoints)
                    .filter(
                        (waypoint) =>
                            waypoint.properties.isLatestImport && !waypoint.properties.archived
                    )
                    .map((waypoint) => ({
                        ...waypoint,
                        properties: {
                            ...waypoint.properties,
                            localId: waypoint.properties.localId,
                            isLatestImport: false,
                        },
                    }))
            ),
        });

        areaDispatcher({
            type: 'updateProperties',
            properties: compact(
                Object.values(areas)
                    .filter((area) => area.properties.isLatestImport && !area.properties.archived)
                    .map((area) => ({
                        localId: area.properties.localId,
                        isLatestImport: false,
                    }))
            ),
        });
    }, [
        importState,
        setImportState,
        setUndo,
        waypointDispatcher,
        trackDispatcher,
        areaDispatcher,
        selections,
        setSelections,
        tracks,
        areas,
        waypoints,
        onSidebarChange,
        folderDispatcher,
    ]);
}
